import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import { CSSProperties } from "@material-ui/styles";
import Checkbox from "@saleor/components/Checkbox";
import Skeleton from "@saleor/components/Skeleton";
import StickyToolbarTableHead from "@saleor/components/StickyToolbarTableHead";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import { SettlementStatusEnum } from "@saleor/types/globalTypes";
import classNames from "classnames";
import moment from "moment-timezone";
import React from "react";
import { FormattedMessage } from "react-intl";

import { FinanceOrderListUrlQueryParams } from "../../urls";
import { PayoutOrders_storeOrdersPayout_edges_node } from "./../../types/PayoutOrders";

const useStyles = makeStyles(
  theme => {
    const dataDisplay: CSSProperties = {
      flex: "60%",
      overflow: "auto",
      textAlign: "left"
    };

    return {
      dataTitle: {
        display: "none !important"
      },
      financeOrdersTbl: {
        [theme.breakpoints.down("md")]: {
          "& thead": {
            display: "none !important"
          },
          "& thead, tbody, th, td, tr": {
            border: "none",
            display: "block"
          },
          "& tr": {
            backgroundColor: "#fff !important",
            borderBottom: "1px solid #ccc",
            paddingBottom: "30px",
            paddingTop: "30px"
          }
        },
        tableLayout: "auto",
        width: "100%"
      },
      [theme.breakpoints.down("md")]: {
        dataDisplay,
        dataGroup: {
          display: "flex"
        },
        dataTitle: {
          display: "block !important",
          flex: "40%",
          fontWeight: "bold",
          textAlign: "left"
        }
      },
      responsiveTable: {
        overflow: "auto !important"
      },
      tableRow: {
        cursor: "pointer" as "pointer"
      },
      tblCol: {
        whiteSpace: "nowrap",
        width: "auto"
      },
      wideColumn: {
        "& :nth-child(1)": {
          width: "12em"
        }
      }
    };
  },
  { name: "FinanceOrderList" }
);

interface FinanceOrderListProp extends ListProps {
  orders: PayoutOrders_storeOrdersPayout_edges_node[];
  isChecked;
  selected;
  toggle;
  toggleAll;
  toolbar;
  params: FinanceOrderListUrlQueryParams;
}

const FinanceOrderList: React.FC<FinanceOrderListProp> = props => {
  const {
    orders,
    disabled,
    settings,
    // sort,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onRowClick,
    // onSort,
    pageInfo,
    isChecked,
    selected,
    toggle,
    toggleAll,
    toolbar,
    params
  } = props;

  const classes = useStyles(props);

  const numberOfColumns = 43;

  return (
    <div className={classes.responsiveTable}>
      <Table className={classes.financeOrdersTbl}>
        <StickyToolbarTableHead
          colSpan={numberOfColumns + 1}
          selected={selected}
          disabled={disabled}
          items={orders}
          toggleAll={toggleAll}
          toolbar={toolbar}
        >
          <TableCellHeader className={classes.tblCol}>
            <FormattedMessage defaultMessage="ID" />
          </TableCellHeader>
          <TableCellHeader className={classes.tblCol}>
            <FormattedMessage defaultMessage="Mall" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Company Name" />
          </TableCellHeader>
          <TableCellHeader>
            <FormattedMessage defaultMessage="Brand" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Vendor Code" />
          </TableCellHeader>
          {/* <TableCellHeader>
            <FormattedMessage defaultMessage="Amount Payment Method" />
          </TableCellHeader> */}
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Order Created Date" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Order Completion Date" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Store Order Code" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Payment Method" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Total Retail Price" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Discount - Tenant" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Discount - SMO" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Total - Discount" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Total Selling Price" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Delivery Fee" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Total Gross Transaction" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Voucher - Tenant" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Voucher - SMO" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Total - Voucher" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Price Paid" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Payment Fee" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="VAT on Payment Fee" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Adjustment on Payment Fee" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Adjustment Vat on Payment Fee" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Other Adjustment" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Other Adjustment Remarks" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Penalties" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Adjustment on Delivery Fee" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Total Payment Fee" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Witholding Tax on Payment Fee" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Creditable Withholding Tax on Card Transaction" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="In Store Cash Payment" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Net Settlement" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Settlement Date" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Payout Status" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Shipping Cost (Actual)" />
          </TableCellHeader>
          <TableCellHeader>
            <FormattedMessage defaultMessage="Rebate" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Insurance" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Transaction Income / (Loss)" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="2307 on Payment Fee Status" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="MDR" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Payment Gateway" />
          </TableCellHeader>
          <TableCellHeader className={classes.wideColumn}>
            <FormattedMessage defaultMessage="Cash Handling Fee" />
          </TableCellHeader>
        </StickyToolbarTableHead>
        <TableBody>
          {orders &&
            renderCollection(
              orders,
              order => {
                const isSelected = order ? isChecked(order?.id) : false;
                return (
                  <TableRow
                    className={classNames(null, {
                      [classes.tableRow]:
                        params.payoutStatus === SettlementStatusEnum.OPEN
                    })}
                    hover={
                      !!order &&
                      params.payoutStatus === SettlementStatusEnum.OPEN
                    }
                    key={order ? order?.id : "skeleton"}
                    selected={isSelected}
                    data-test="id"
                    data-test-id={maybe(() => order.id)}
                    onClick={
                      params.payoutStatus === SettlementStatusEnum.OPEN
                        ? onRowClick(order.id)
                        : null
                    }
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        disabled={disabled}
                        disableClickPropagation
                        onChange={() => toggle(order.id)}
                      />
                    </TableCell>
                    <TableCell data-test="id" className={classes.tblCol}>
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Mall</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.id, null) ? (
                            atob(order.id)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell data-test="mall" className={classes.tblCol}>
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Mall</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.mall_name, null) ? (
                            order.mall_name.toUpperCase()
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="companyName"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Company Name</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.legal_name, null) ? (
                            order.legal_name.toUpperCase()
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell data-test="brand" className={classes.tblCol}>
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Brand</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.brand_name, null) ? (
                            order.brand_name.toUpperCase()
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="vendorCode"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Brand</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.vendor_code, null) ? (
                            order.vendor_code
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>

                    {/* <TableCell data-test="payment" className={classes.tblCol}>
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Store Order Code
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.payment_method_type, null) ? (
                            order.payment_method_type
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell> */}
                    <TableCell data-test="created" className={classes.tblCol}>
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Store Order Code
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.order_date, null) ? (
                            moment(order.order_date).format("LLL")
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="completedAt"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Store Order Code
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.completed_at, null) ? (
                            moment(order.completed_at).format("LLL")
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="storeOrderCode"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Store Order Code
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.store_order_code, null) ? (
                            order.store_order_code
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="paymentMethod"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Payment Method</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.payment_method_type, null) ? (
                            order.payment_method_type.toUpperCase()
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="totalRetailPrice"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Total Retail Price
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.retail_price, null) ? (
                            order.retail_price.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="discountTenant"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Discount - Tenant
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.sales_discount_tenant, null) ? (
                            order.sales_discount_tenant.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="discountSMO"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Discount - SMO</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.sales_discount_sm, null) ? (
                            order.sales_discount_sm.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="totalDiscount"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Total Discount</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.sales_discount_total, null) ? (
                            order.sales_discount_total.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="totalDiscount"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Total Selling Price
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.selling_price, null) ? (
                            order.selling_price.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="deliveryFee"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Delivery Fee</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.delivery_fee, null) ? (
                            order.delivery_fee.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="totalGrossTransaction"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Total Gross Transaction
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.total_gross_transaction, null) ? (
                            order.total_gross_transaction.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="voucherTenant"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Voucher - Tenant
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.voucher_discount_tenant, null) ? (
                            order.voucher_discount_tenant.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="voucherSMO"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Voucher - SMO </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.voucher_discount_sm, null) ? (
                            order.voucher_discount_sm.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="totalVoucher"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Total - Voucher</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.voucher_discount_total, null) ? (
                            order.voucher_discount_total.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell data-test="pricePaid" className={classes.tblCol}>
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Price Paid</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.price_paid, null) ? (
                            order.price_paid.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="paymentFee"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Payment Fee</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.payment_fee, null) ? (
                            order.payment_fee.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="paymentFeeVat"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Payment Fee Vat</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.payment_fee_vat, null) ? (
                            order.payment_fee_vat
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="adjustmentOnPaymentFee"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Adjustment on Payment Fee
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.payment_fee_adjustment, null) ? (
                            order.payment_fee_adjustment.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="adjustmentVatOnPaymentFee"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Adjustment Vat on Payment Fee
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(
                            () => order.payment_fee_adjustment_vat,
                            null
                          ) ? (
                            order.payment_fee_adjustment_vat.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="otherAdjustment"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Other Adjustment
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.other_adjustment, null) ? (
                            order.other_adjustment.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="otherAdjustment"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Other Adjustment Remarks
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.other_remarks, null) ? (
                            order.other_remarks
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell data-test="penalties" className={classes.tblCol}>
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Penalties</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.penalties, null) ? (
                            order.penalties.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="deliveryFeeAdjustment"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Adjustment on Delivery Fee
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.delivery_fee_adjustment, null) ? (
                            order.delivery_fee_adjustment.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="totalPaymentFee"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Total Payment Fee
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.total_payment_fee, null) ? (
                            order.total_payment_fee.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="witholdingTaxOnPaymentFEe"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Witholding Tax on Payment Fee
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(
                            () => order.payment_fee_witholding_tax,
                            null
                          ) ? (
                            order.payment_fee_witholding_tax.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="witholdingTaxOnCreditCardTransaction"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Witholding Tax on Credit Card Transaction
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(
                            () => order.credit_card_witholding_tax,
                            null
                          ) ? (
                            order.credit_card_witholding_tax.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="witholdingTaxOnCreditCardTransaction"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          In Store Cash Payment
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.in_store_cash_payment, null) ? (
                            order.in_store_cash_payment.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="netSettlement"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Net Settlement</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.net_settlement, null) ? (
                            order.net_settlement.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="settlementDate"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Settlement Date</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.settlement_date, null) ? (
                            order.settlement_date
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="settlementDate"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Payout Status</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.payout_status, null) ? (
                            order.payout_status
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="shippingCost"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Shipping Cost (Actual)
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.shipping_cost.toFixed(2), null) ? (
                            order.shipping_cost
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell data-test="rebate" className={classes.tblCol}>
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Rebate </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.rebate_amount, null) ? (
                            order.rebate_amount
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell data-test="insurance" className={classes.tblCol}>
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Insurance</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.insurance, null) ? (
                            order.insurance
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="transactionIncome"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Transaction Income
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.transaction_income, null) ? (
                            order.transaction_income.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="paymentFee2307"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          2307 on Payment Fee Status
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.payment_fee_2307, null) ? (
                            order.payment_fee_2307.toFixed(2)
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell data-test="mdr" className={classes.tblCol}>
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>MDR</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.mdr, null) ? (
                            order.mdr
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="paymentGateway"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>Payment Gateway</div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.payment_gateway, null) ? (
                            order.payment_gateway
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      data-test="cashHandlingFee"
                      className={classes.tblCol}
                    >
                      <div className={classes.dataGroup}>
                        <div className={classes.dataTitle}>
                          Cash Handling Fee
                        </div>
                        <div className={classes.dataDisplay}>
                          {maybe(() => order.cash_handling_fee, null) ? (
                            order.cash_handling_fee
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              },
              () => (
                <TableRow>
                  <TableCell colSpan={numberOfColumns}>
                    <FormattedMessage defaultMessage="No orders found" />
                  </TableCell>
                </TableRow>
              )
            )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={numberOfColumns + 1}
              settings={settings}
              hasNextPage={
                pageInfo && !disabled ? pageInfo.hasNextPage : undefined
              }
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
};

export default FinanceOrderList;
